import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';

import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Button, PressableType } from 'app/design/primitives/button';
import { AddPhoneNumber } from 'app/design/icons/add-phone-number';
import { ControlledInput } from 'app/design/primitives/input/controlled';

import { useForm } from 'app/hooks/form';
import * as yup from 'yup';
import { useUpsertMyProfile } from 'app/network/profile';
import { type UpdateProfileDTO } from 'app/types/profile';
import { Pressable } from 'react-native-gesture-handler';
import { useState } from 'react';
import { Modal } from 'app/design/primitives/modal';
import { track, Event } from 'app/lib/analytics';
import { useUser } from 'app/network/user';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function PromptPhoneNumber({ open, onClose }: { open?: boolean; onClose?: () => void }) {
  const { t } = useTranslation('all-listings');
  const { t: t2 } = useTranslation('personal-information');

  const schema: yup.ObjectSchema<Pick<UpdateProfileDTO, 'phone'>> = yup.object({
    phone: yup
      .string()
      .trim()
      .defined()
      .required()
      .transform((value: string) => {
        if (!value) return value;
        // Keep only digits, - . spaces and leading "+"
        return value.replace(/[^+\d-. ]/g, '');
      }),
    // Not working - form always show this field as invalid when we change the value; disabling for now
    // .test('is-valid-phone', t('phoneFieldError'), function (value) {
    //   if (!value) return false;
    //   return phoneRegExp.test(value);
    // }),
  });

  const { control, handleSubmit, formState } = useForm({
    schema,
    defaultValues: {
      phone: '',
    },
  });

  const [, setBannerSeen] = useStoredValue<boolean>(StorageKey.PromptPhoneNumberBannerSeen, false);

  const { mutate, isPending } = useUpsertMyProfile({
    onSuccess: () => {
      onClose?.();
      setBannerSeen(true);
    },
  });

  const { data: user } = useUser();
  const onSubmit = handleSubmit((form: Pick<UpdateProfileDTO, 'phone'>) => {
    track(Event.SubmittedPromptPhoneNumber, {
      user_id: user?.id ?? '',
    });
    mutate(form);
  });

  const [isInfoOpen, setIsInfoOpen] = useState(false);

  return (
    <BottomSheet
      containerStyle={{ paddingHorizontal: theme.spacing['spacing.general.x6'] }}
      onClose={onClose}
      open={open}
      showIndicator={false}
    >
      <Button
        onPress={onClose}
        pressableType={PressableType.GestureHandler}
        style={{ position: 'absolute', top: -10, left: -20 }}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} size={25} />
      </Button>
      <Column gap="spacing.general.x8">
        <Row justifyContent="center">
          <AddPhoneNumber height={50} width={130} />
        </Row>
        <View>
          <ControlledInput
            {...{ control }}
            autoComplete="tel"
            label={t2('phoneFieldLabel')}
            name="phone"
            textContentType="telephoneNumber"
            labelRight={
              <Pressable
                onPress={() => setIsInfoOpen(true)}
                style={{ position: 'relative', bottom: 5, right: 5, marginBottom: -10 }}
              >
                <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
              </Pressable>
            }
          />
          <Text variant="bodyBase" style={{ marginTop: 4, color: theme.colors['text.tertiary'] }}>
            {t2('phoneFieldSubtitle')}
          </Text>
        </View>
        <Button variant="primary" onPress={() => void onSubmit()} disabled={!formState.isValid} loading={isPending}>
          {t('addPhoneNumberPrompt.bottom-sheet.button')}
        </Button>
      </Column>

      <Modal onClose={() => setIsInfoOpen(false)} open={isInfoOpen} transparent>
        <Modal.Card>
          <Modal.Title allowClose onClose={() => setIsInfoOpen(false)}>
            {t2('phoneFieldHelp.title')}
          </Modal.Title>

          <Column
            gap="spacing.general.x4"
            style={{
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              paddingBottom: theme.spacing['spacing.general.x8'],
            }}
          >
            <Text fontWeight={700} variant="headerXSmall">
              {t2('phoneFieldHelp.boldText')}
            </Text>
            <Text variant="bodyBase" fontSize={16} lineHeight={22}>
              {t2('phoneFieldHelp.text')}
            </Text>
          </Column>
        </Modal.Card>
      </Modal>
    </BottomSheet>
  );
}
